import React, { useEffect, useState, useMemo } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Loader2, Plus, Trash2, Receipt, DollarSign,
  ChevronDown, ChevronUp, Banknote, CheckCircle2,
} from "lucide-react";
import { motion } from "framer-motion";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function StatusChip({ status }) {
  const paid = status === "paid";
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] rounded-full font-medium"
      style={
        paid
          ? { color: "#8A8A8A", background: "rgba(138,138,138,0.08)" }
          : { color: "#FF9800", background: "rgba(255,152,0,0.08)" }
      }
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: paid ? "#8A8A8A" : "#FF9800" }} />
      {paid ? "Paid" : "Unpaid"}
    </span>
  );
}

function SplitRow({ split, expenseId, canEdit, onCashPaid }) {
  const [cashAmt, setCashAmt] = useState("");
  const [showCash, setShowCash] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const owed = parseFloat(split.owed || 0);
  const paid = parseFloat(split.paid || 0);
  const outstanding = Math.max(0, owed - paid);
  const pctPaid = owed > 0 ? Math.min(100, (paid / owed) * 100) : 100;
  const fullyPaid = outstanding < 0.01;

  const submitCash = async () => {
    const amt = parseFloat(cashAmt);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setSubmitting(true);
    try {
      await api.post(`/admin/expenses/v2/${expenseId}/cash-payment`, {
        payer_uid: split.uid,
        amount: amt,
      });
      toast.success("Cash payment recorded");
      setCashAmt("");
      setShowCash(false);
      onCashPaid();
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-lg p-3 mb-2">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-foreground font-medium">{split.email}</span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted font-mono">{split.percent}%</span>
          <span
            className="text-sm font-mono font-semibold"
            style={{ color: fullyPaid ? "#8A8A8A" : "var(--foreground)" }}
          >
            {money(outstanding)} outstanding
          </span>
          {canEdit && !fullyPaid && (
            <button
              onClick={() => setShowCash(!showCash)}
              className="text-faint hover:text-gold transition-colors"
              title="Record cash payment"
            >
              <Banknote size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 rounded-full overflow-hidden mb-1" style={{ background: "rgba(255,255,255,0.15)" }}>
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${pctPaid}%`,
            background: fullyPaid ? "#8A8A8A" : "rgba(138,138,138,0.5)",
          }}
        />
      </div>
      <div className="flex justify-between text-[11px] text-faint">
        <span>{money(paid)} paid · {money(owed)} total</span>
        <span style={{ color: fullyPaid ? "#8A8A8A" : undefined }}>
          {pctPaid.toFixed(0)}% paid off
        </span>
      </div>

      {/* Cash payments log */}
      {(split.cash_payments || []).length > 0 && (
        <div className="mt-2 space-y-0.5">
          {split.cash_payments.map((cp, i) => (
            <p key={i} className="text-[11px] text-faint">
              Cash {money(cp.amount)} — recorded by {cp.recorded_by} on{" "}
              {cp.recorded_at?.slice(0, 10)}
            </p>
          ))}
        </div>
      )}

      {/* Cash input */}
      {showCash && canEdit && (
        <div className="mt-3 flex gap-2 items-center">
          <input
            type="number"
            step="0.01"
            min="0.01"
            max={outstanding}
            className="input-luxe flex-1"
            placeholder={`Up to ${money(outstanding)}`}
            value={cashAmt}
            onChange={(e) => setCashAmt(e.target.value)}
          />
          <button className="btn-primary" onClick={submitCash} disabled={submitting} style={{ padding: "8px 14px", fontSize: "12px" }}>
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Banknote size={12} />} Record Cash
          </button>
          <button className="btn-secondary" onClick={() => setShowCash(false)} style={{ padding: "8px 12px", fontSize: "12px" }}>
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

function ExpenseCard({ expense, currentUid, currentRole, admins, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);

  const isOwner = currentRole === "owner";
  const isFiler = expense.logged_by_uid === currentUid;
  const canEdit = isOwner || isFiler;

  const totalPaid = (expense.splits || []).reduce((acc, s) => acc + parseFloat(s.paid || 0), 0);
  const pctPaid = expense.amount > 0 ? Math.min(100, (totalPaid / expense.amount) * 100) : 100;
  const isPaid = expense.status === "paid";

  const markPaid = async () => {
    if (!window.confirm("Mark this entire expense as paid?")) return;
    setMarkingPaid(true);
    try {
      await api.post(`/admin/expenses/v2/${expense.id}/mark-paid`);
      toast.success("Expense marked as paid");
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    } finally {
      setMarkingPaid(false);
    }
  };

  return (
    <div
      className="card-luxe mb-3 overflow-hidden"
    >
      {/* Header row */}
      <button
        className="w-full text-left p-5 flex items-center justify-between gap-4 hover:bg-foreground/[0.02] transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <StatusChip status={expense.status} />
            <span className="text-[10px] text-faint font-mono">{expense.id}</span>
            <span className="text-[10px] text-faint">{expense.date}</span>
          </div>
          <p
            className="text-foreground font-medium text-sm truncate"
            style={isPaid ? { color: "#8A8A8A" } : undefined}
          >
            {expense.item}
          </p>
          <p className="text-faint text-xs mt-0.5">Filed by {expense.logged_by_email}</p>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <p
              className="font-mono font-semibold"
              style={isPaid ? { color: "#8A8A8A" } : { color: "var(--foreground)" }}
            >
              {money(expense.amount)}
            </p>
            <p className="text-[11px] text-faint">{pctPaid.toFixed(0)}% paid off</p>
          </div>
          {expanded ? <ChevronUp size={16} className="text-muted" /> : <ChevronDown size={16} className="text-muted" />}
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-5 pb-5 pt-4">
          {/* Progress */}
          <div className="mb-4">
            <div className="flex justify-between text-xs text-faint mb-1">
              <span>Total paid: {money(totalPaid)}</span>
              <span>Total: {money(expense.amount)}</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.15)" }}>
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${pctPaid}%`,
                  background: isPaid ? "#8A8A8A" : "rgba(138,138,138,0.5)",
                }}
              />
            </div>
          </div>

          {expense.note && (
            <div className="mb-4">
              <p className="overline mb-1">Description</p>
              <p className="text-sm text-muted">{expense.note}</p>
            </div>
          )}

          <p className="overline mb-3">Who Owes What</p>
          {(expense.splits || []).map((s) => (
            <SplitRow
              key={s.uid}
              split={s}
              expenseId={expense.id}
              canEdit={canEdit}
              onCashPaid={onRefresh}
            />
          ))}

          {/* Actions */}
          {canEdit && !isPaid && (
            <div className="flex gap-2 mt-4 justify-end">
              <button
                className="btn-primary"
                onClick={markPaid}
                disabled={markingPaid}
                style={{ padding: "8px 14px", fontSize: "12px" }}
              >
                {markingPaid ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={12} />
                )}
                Mark Fully Paid
              </button>
            </div>
          )}
          {isPaid && (
            <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
              <p className="text-sm" style={{ color: "#8A8A8A" }}>
                ✓ Paid off{expense.paid_at ? ` on ${expense.paid_at.slice(0, 10)}` : ""}
                {expense.paid_by ? ` by ${expense.paid_by}` : ""}
              </p>
              {isOwner && (
                <button
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded transition-colors"
                  style={{ color: "#F44336", background: "rgba(244,67,54,0.06)" }}
                  onClick={async () => {
                    if (!window.confirm("Permanently delete this paid expense? This cannot be undone.")) return;
                    try {
                      await api.delete(`/admin/expenses/v2/${expense.id}`);
                      toast.success("Expense deleted");
                      onRefresh();
                    } catch (e) { toast.error(errMsg(e, "Failed to delete")); }
                  }}
                >
                  <Trash2 size={13} /> Delete permanently
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminExpenses() {
  const { user, role } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unpaid | paid
  const [createOpen, setCreateOpen] = useState(false);

  // New expense form
  const [form, setForm] = useState({
    item: "",
    amount: "",
    date: fmt(new Date()),
    note: "",
    splits: [],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [e, u] = await Promise.all([
        api.get("/admin/expenses/v2"),
        api.get("/admin/users"),
      ]);
      setExpenses(e.data.expenses || []);
      setAdmins(u.data.users || []);
    } catch (err) {
      toast.error(errMsg(err, "Failed to load expenses"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isOwner = role === "owner";

  const filtered = useMemo(() => {
    if (filter === "unpaid") return expenses.filter((e) => e.status === "unpaid");
    if (filter === "paid") return expenses.filter((e) => e.status === "paid");
    return expenses;
  }, [expenses, filter]);

  // Splits form helpers
  const addSplit = () => setForm((f) => ({ ...f, splits: [...f.splits, { uid: "", email: "", percent: 0 }] }));
  const updateSplit = (i, key, val) => {
    setForm((f) => {
      const splits = [...f.splits];
      if (key === "uid") {
        const u = admins.find((a) => a.uid === val);
        splits[i] = { uid: val, email: u?.email || "", percent: splits[i].percent };
      } else {
        splits[i] = { ...splits[i], [key]: val };
      }
      return { ...f, splits };
    });
  };
  const removeSplit = (i) => setForm((f) => ({ ...f, splits: f.splits.filter((_, idx) => idx !== i) }));

  const submitExpense = async (e) => {
    e.preventDefault();
    if (form.splits.length === 0) return toast.error("Assign at least one person to this expense");
    const total = form.splits.reduce((acc, s) => acc + (parseFloat(s.percent) || 0), 0);
    if (Math.abs(total - 100) > 0.5) return toast.error(`Splits must total 100% (got ${total.toFixed(0)}%)`);
    const hasBlanks = form.splits.some((s) => !s.uid);
    if (hasBlanks) return toast.error("All split rows must have a person selected");
    try {
      await api.post("/admin/expenses/v2", {
        item: form.item,
        amount: parseFloat(form.amount),
        date: form.date,
        note: form.note,
        splits: form.splits.map((s) => ({ ...s, percent: parseFloat(s.percent) })),
      });
      toast.success("Expense logged");
      setCreateOpen(false);
      setForm({ item: "", amount: "", date: fmt(new Date()), note: "", splits: [] });
      load();
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    }
  };

  const totalOutstanding = expenses
    .filter((e) => e.status === "unpaid")
    .reduce((acc, e) => {
      const paid = (e.splits || []).reduce((a, s) => a + parseFloat(s.paid || 0), 0);
      return acc + (parseFloat(e.amount) - paid);
    }, 0);

  return (
    <div data-testid="admin-expenses">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
        <div>
          <p className="overline mb-2">Expenses</p>
          <h1 className="font-sans font-semibold text-4xl text-foreground tracking-[-0.02em]">
            Shared expenses
          </h1>
        </div>
        <button className="btn-primary" onClick={() => setCreateOpen(true)}>
          <Plus size={14} /> Log expense
        </button>
      </div>

      {/* Summary stat */}
      <div className="card-luxe p-5 mb-6 flex gap-8">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-faint mb-1">Outstanding</p>
          <p className="font-mono text-lg text-foreground">{money(totalOutstanding)}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-faint mb-1">Total expenses</p>
          <p className="font-mono text-lg text-foreground">{expenses.length}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-faint mb-1">Unpaid</p>
          <p className="font-mono text-lg" style={{ color: "#FF9800" }}>
            {expenses.filter((e) => e.status === "unpaid").length}
          </p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-5">
        {["all", "unpaid", "paid"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="px-4 py-1.5 text-xs uppercase tracking-[0.18em] transition-all"
            style={
              filter === f
                ? { color: "#8A8A8A", background: "rgba(138,138,138,0.08)" }
                : { color: "rgba(255,255,255,0.5)" }
            }
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card-luxe p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-5 w-24" />
              </div>
              <Skeleton className="h-4 w-2/3 mb-2" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-luxe p-12 text-center">
          <Receipt size={32} className="text-muted mx-auto mb-3" />
          <p className="text-muted">No {filter === "all" ? "" : filter} expenses.</p>
        </div>
      ) : (
        <div>
          {filtered.map((exp) => (
            <ExpenseCard
              key={exp.id}
              expense={exp}
              currentUid={user?.uid}
              currentRole={role}
              admins={admins}
              onRefresh={load}
            />
          ))}
        </div>
      )}

      {/* Create expense modal */}
      {createOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            onClick={() => setCreateOpen(false)}
          />
          <div className="relative card-luxe p-7 max-w-xl w-full max-h-[92vh] overflow-y-auto">
            <h3 className="font-sans font-semibold text-2xl text-foreground mb-5">Log expense</h3>
            <form onSubmit={submitExpense} className="space-y-4">
              <FormField label="What was purchased">
                <input
                  className="input-luxe"
                  required
                  placeholder="Foam cannon, chemicals, towels…"
                  value={form.item}
                  onChange={(e) => setForm({ ...form, item: e.target.value })}
                />
              </FormField>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="Total amount (USD)">
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="input-luxe"
                    required
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  />
                </FormField>
                <FormField label="Date">
                  <div className="flex gap-2">
                    <input
                      type="date"
                      className="input-luxe flex-1"
                      required
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ padding: "8px 10px", fontSize: "11px" }}
                      onClick={() => setForm({ ...form, date: fmt(new Date()) })}
                    >
                      Today
                    </button>
                  </div>
                </FormField>
              </div>

              <FormField label="Description">
                <textarea
                  className="input-luxe"
                  rows={2}
                  placeholder="Additional details about this purchase…"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                />
              </FormField>

              <div>
                <p className="overline mb-2">Who owes what (splits must total 100%)</p>
                <div className="space-y-2 mb-3">
                  {form.splits.map((s, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <select
                        className="input-luxe flex-1"
                        value={s.uid}
                        onChange={(e) => updateSplit(i, "uid", e.target.value)}
                      >
                        <option value="">Pick person…</option>
                        {admins.map((a) => (
                          <option key={a.uid} value={a.uid}>
                            {a.email}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        className="input-luxe w-24"
                        placeholder="%"
                        value={s.percent}
                        onChange={(e) => updateSplit(i, "percent", e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => removeSplit(i)}
                        className="text-faint hover:text-red-400 p-2"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ padding: "8px 14px", fontSize: "12px" }}
                  onClick={addSplit}
                >
                  <Plus size={12} /> Add person
                </button>

                {/* Live split total indicator */}
                {form.splits.length > 0 && (
                  <p
                    className="text-xs mt-2"
                    style={{
                      color:
                        Math.abs(
                          form.splits.reduce((a, s) => a + (parseFloat(s.percent) || 0), 0) - 100
                        ) < 0.5
                          ? "#4CAF50"
                          : "#FF9800",
                    }}
                  >
                    Total: {form.splits.reduce((a, s) => a + (parseFloat(s.percent) || 0), 0).toFixed(0)}% (must be 100%)
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setCreateOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  <Receipt size={14} /> Log expense
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="overline block mb-2">{label}</span>
      {children}
    </label>
  );
}
